/**
 * Vello color tokens (S055; extraction contract §1 `@vello/tokens`, IMPORT-0001 §3/§4).
 *
 * Values are captured **verbatim** from the authoritative Vello Claude Design project
 * (`00a6290b-…`, `tokens/colors.css`) — nothing here is invented. The brand is near-monochrome: `red` and
 * `green` exist **only** as semantic + map-pin colors, never as a logo, section background, or primary
 * button (SKILL.md / IMPORT-0001 §4). The primary action is **ink navy `#233048`** (the aug/2026 Design
 * System decision that replaced pure black) in light and inverts to snow (a white button) in dark. Every
 * value is a color; no locale, currency, or country lives here.
 *
 * Color is exposed as named **roles** (`surface.page`, `text.primary`, `action.primary`, …) rather than
 * raw hex, so a consumer references a role and the theme supplies the value. Raw hex literals live only in
 * this file and its sibling token files — the `check:visual` gate forbids them anywhere else.
 */
/** The two brand-inherited colors. Semantic + map pins only — never brand accent, logo, or CTA. */
export const BRAND = {
    red: '#F0122E',
    green: '#10C554',
};
/** Named brand anchors from the brand kickoff. `ink` is the aug/2026 navy that replaced pure black. */
export const ANCHOR = {
    ink: '#233048',
    graphite: '#202127',
    mist: '#F5F6F8',
    snow: '#FFFFFF',
};
/** The neutral ramp (`gray-50 … gray-900`), theme-invariant. */
export const GRAY = {
    50: '#F5F6F8',
    100: '#EEEFF2',
    200: '#E3E5E8',
    300: '#D2D5DA',
    400: '#A6AAB2',
    500: '#7A7E88',
    600: '#54575F',
    700: '#33363D',
    800: '#202127',
    900: '#0B0F1A',
};
/** Light theme color roles — verbatim `:root` of `tokens/colors.css`. */
export const LIGHT_COLORS = {
    'surface.page': '#FFFFFF',
    'surface.subtle': '#F5F6F8',
    'surface.card': '#FFFFFF',
    'surface.inverse': '#233048',
    'surface.overlay': 'rgba(35, 48, 72, 0.48)',
    'surface.footer': '#FAFAFB',
    'surface.glass': 'rgba(255, 255, 255, 0.82)',
    'surface.nav': '#FFFFFF',
    'skeleton.base': '#EDEFF2',
    'skeleton.peak': '#F7F8FA',
    'scrim.drawer': 'rgba(35, 48, 72, 0.28)',
    'scrim.modal': 'rgba(35, 48, 72, 0.40)',
    'scrim.palette': 'rgba(35, 48, 72, 0.35)',
    'scrim.photo': 'rgba(35, 48, 72, 0.55)',
    'scrim.sheet': 'rgba(14, 22, 38, 0.45)',
    'chip.queue': '#E9EDF5',
    'text.primary': '#233048',
    'text.secondary': '#54575F',
    'text.tertiary': '#7A7E88',
    'text.disabled': '#A6AAB2',
    'text.inverse': '#FFFFFF',
    'border.subtle': '#EEEFF2',
    'border.default': '#E3E5E8',
    'border.strong': '#233048',
    'border.hover': '#A6AAB2',
    'border.control': '#D2D5DA',
    'border.kpi-hover': '#C9CDD4',
    'border.fill': '#F5F6F8',
    'on-inverse.primary': '#FFFFFF',
    'on-inverse.secondary': 'rgba(255, 255, 255, 0.72)',
    'on-inverse.tertiary': 'rgba(255, 255, 255, 0.62)',
    'on-inverse.muted': 'rgba(255, 255, 255, 0.55)',
    'on-inverse.disabled': 'rgba(255, 255, 255, 0.40)',
    'on-inverse.hover': 'rgba(255, 255, 255, 0.10)',
    'on-inverse.hover-strong': 'rgba(255, 255, 255, 0.12)',
    'on-inverse.press': 'rgba(255, 255, 255, 0.22)',
    'on-inverse.press-strong': 'rgba(255, 255, 255, 0.24)',
    'on-inverse.flash': 'rgba(255, 255, 255, 0.30)',
    'on-inverse.chip': 'rgba(255, 255, 255, 0.12)',
    'on-inverse.chip-strong': 'rgba(255, 255, 255, 0.14)',
    'on-inverse.chip-hover': 'rgba(255, 255, 255, 0.24)',
    'on-inverse.life': 'rgba(255, 255, 255, 0.35)',
    'on-inverse.divider': 'rgba(255, 255, 255, 0.12)',
    'on-inverse.field': '#1C2940',
    'on-inverse.field-border': '#394A68',
    'on-inverse.field-border-hover': '#6F80A3',
    'on-inverse.field-focus': '#C9D1E0',
    'on-inverse.icon': '#8FA0C0',
    'on-inverse.action-hover': '#EEF1F6',
    'action.primary': '#233048',
    'action.primary.hover': '#2C3B56',
    'action.primary.press': '#394A68',
    'action.primary.disabled': 'rgba(35, 48, 72, 0.38)',
    'action.on-primary': '#FFFFFF',
    'action.secondary': '#F5F6F8',
    'action.secondary.hover': '#EEEFF2',
    'action.secondary.press': '#E3E5E8',
    'success': '#10C554',
    'success.soft': '#E4F9ED',
    'success.text': '#0A7A36',
    'success.hover': '#E2F8EB',
    'success.press': '#CEF4DD',
    'success.pulse': 'rgba(16, 197, 84, 0.5)',
    'danger': '#F0122E',
    'danger.soft': '#FDE8EB',
    'danger.text': '#C20D26',
    'danger.hover': '#FCDDE2',
    'danger.press': '#FCD3D8',
    'danger.solid': '#C20D26',
    'danger.solid-hover': '#A80B21',
    'danger.solid-press': '#8F091C',
    'warning': '#F5A300',
    'warning.soft': '#FBF1DC',
    'warning.text': '#8A5B00',
    'danger.on': '#FFFFFF',
    'warning.on': '#233048',
    'danger.badge': '#C20D26',
};
/** Dark theme color roles — `:root` with the `[data-theme="dark"]` overrides applied (verbatim). */
export const DARK_COLORS = {
    'surface.page': '#0E1626',
    'surface.subtle': '#141E32',
    'surface.card': '#1C2940',
    'surface.inverse': '#FFFFFF',
    'surface.overlay': 'rgba(0, 0, 0, 0.6)',
    'surface.footer': '#101A2D',
    'surface.glass': 'rgba(14, 22, 38, 0.82)',
    'surface.nav': '#1C2940',
    'skeleton.base': '#202B42',
    'skeleton.peak': '#2B3A57',
    'scrim.drawer': 'rgba(0, 0, 0, 0.5)',
    'scrim.modal': 'rgba(0, 0, 0, 0.6)',
    'scrim.palette': 'rgba(0, 0, 0, 0.55)',
    'scrim.photo': 'rgba(0, 0, 0, 0.7)',
    'scrim.sheet': 'rgba(0, 0, 0, 0.6)',
    'chip.queue': '#202B42',
    'text.primary': '#FFFFFF',
    'text.secondary': '#A6AAB2',
    'text.tertiary': '#7A7E88',
    'text.disabled': '#54575F',
    'text.inverse': '#233048',
    'border.subtle': '#202B42',
    'border.default': '#2B3A57',
    'border.strong': '#FFFFFF',
    'border.hover': '#6F80A3',
    'border.control': '#394A68',
    'border.kpi-hover': '#394A68',
    'border.fill': '#141E32',
    // Dark inverts the inverse surface to snow, so what sits on it is the navy ramp at the same alphas.
    'on-inverse.primary': '#233048',
    'on-inverse.secondary': 'rgba(35, 48, 72, 0.72)',
    'on-inverse.tertiary': 'rgba(35, 48, 72, 0.62)',
    'on-inverse.muted': 'rgba(35, 48, 72, 0.55)',
    'on-inverse.disabled': 'rgba(35, 48, 72, 0.40)',
    'on-inverse.hover': 'rgba(35, 48, 72, 0.08)',
    'on-inverse.hover-strong': 'rgba(35, 48, 72, 0.10)',
    'on-inverse.press': 'rgba(35, 48, 72, 0.18)',
    'on-inverse.press-strong': 'rgba(35, 48, 72, 0.20)',
    'on-inverse.flash': 'rgba(35, 48, 72, 0.24)',
    'on-inverse.chip': 'rgba(35, 48, 72, 0.10)',
    'on-inverse.chip-strong': 'rgba(35, 48, 72, 0.12)',
    'on-inverse.chip-hover': 'rgba(35, 48, 72, 0.20)',
    'on-inverse.life': 'rgba(35, 48, 72, 0.30)',
    'on-inverse.divider': 'rgba(35, 48, 72, 0.12)',
    'on-inverse.field': '#F5F6F8',
    'on-inverse.field-border': '#E3E5E8',
    'on-inverse.field-border-hover': '#A6AAB2',
    'on-inverse.field-focus': '#233048',
    'on-inverse.icon': '#7A7E88',
    'on-inverse.action-hover': '#2C3B56',
    'action.primary': '#FFFFFF',
    'action.primary.hover': '#E3E5E8',
    'action.primary.press': '#D2D5DA',
    'action.primary.disabled': 'rgba(255, 255, 255, 0.38)',
    'action.on-primary': '#233048',
    'action.secondary': '#202B42',
    'action.secondary.hover': '#2B3A57',
    'action.secondary.press': '#394A68',
    'success': '#10C554',
    'success.soft': 'rgba(16, 197, 84, 0.14)',
    'success.text': '#3BDE7C',
    'success.hover': 'rgba(16, 197, 84, 0.22)',
    'success.press': 'rgba(16, 197, 84, 0.3)',
    'success.pulse': 'rgba(16, 197, 84, 0.5)',
    'danger': '#F0122E',
    'danger.soft': 'rgba(240, 18, 46, 0.14)',
    'danger.text': '#FF6A7C',
    'danger.hover': 'rgba(240, 18, 46, 0.2)',
    'danger.press': 'rgba(240, 18, 46, 0.26)',
    'danger.solid': '#FF6A7C',
    'danger.solid-hover': '#FF8190',
    'danger.solid-press': '#FF96A2',
    'warning': '#F5A300',
    'warning.soft': 'rgba(245, 163, 0, 0.14)',
    'warning.text': '#FFC24D',
    'danger.on': '#FFFFFF',
    'warning.on': '#233048',
    'danger.badge': '#C20D26',
};
/**
 * The web consoles' light (D854, DS-1: the audit of the D845 round measured 4.06:1 on captions, breadcrumbs and the ⌘K field and
 * 3.76:1 on the table headers): the design's light ({@link LIGHT_COLORS}) with the tertiary text role lifted for AA on the page, a
 * card and the mist fill. `#7A7E88` (4.06:1 on white, 3.76:1 on `surface.subtle`) becomes `#666A74` (5.4:1 on white, 5.0:1 on
 * `surface.subtle`, 4.7:1 on the 100-gray hover fill), still a step below `text.secondary` `#54575F`. The native apps keep the
 * design's `#7A7E88` (LIGHT_COLORS); the web dark already lifts the same role ({@link WEB_DARK_COLORS}).
 */
export const WEB_LIGHT_COLORS = {
    ...LIGHT_COLORS,
    'text.tertiary': '#666A74',
};
/**
 * The native apps' dark theme (Rider and Driver) — Claude Design `App Cliente - Dark Theme Revisão.dc.html`, direction
 * **1a "Azul-noite elevado"**, chosen by the owner on 2026-10-27 (D763) because the web dark (`DARK_COLORS`, page
 * `#0E1626`) read as black on a phone. Only surfaces, edges and the secondary text change; layout, type, icons, the white
 * primary action and the semantic colors stay the `[data-theme="dark"]` values. Verbatim from the design: page `#1B2538`,
 * filled controls `#2A3750` with a 1px `#364461` edge, dividers and the nav top edge `#2F3C57`, nav `#212D45`, the floating
 * map button `#2E3C58` with a `#3F4E6C` edge, secondary text `#B3B9C6`, inactive nav labels `#8A93A6`. Since D798 the web
 * consoles' dark is this navy too (`WEB_DARK_THEME`); `DARK_COLORS` stays the Painel TV wall's.
 */
export const APP_DARK_COLORS = {
    ...DARK_COLORS,
    'surface.page': '#1B2538',
    'surface.subtle': '#2A3750',
    'surface.card': '#2E3C58',
    'surface.nav': '#212D45',
    'surface.footer': '#212D45',
    'surface.glass': 'rgba(27, 37, 56, 0.82)',
    'skeleton.base': '#2A3750',
    'skeleton.peak': '#364461',
    'chip.queue': '#2A3750',
    'text.secondary': '#B3B9C6',
    'text.tertiary': '#8A93A6',
    'border.subtle': '#2F3C57',
    'border.default': '#364461',
    'border.control': '#3F4E6C',
    'border.kpi-hover': '#3F4E6C',
    'border.fill': '#364461',
    'action.secondary': '#2A3750',
    'action.secondary.hover': '#2E3C58',
    'action.secondary.press': '#364461',
};
/**
 * The web consoles' dark (D798): the apps' navy ({@link APP_DARK_COLORS}) with text roles lifted for AA (two in D798, three more in D803). On a phone the
 * text mostly sits on the page; in a console it sits on the raised cards (`surface.card` `#2E3C58`), where the apps'
 * tertiary `#8A93A6` reads 3.6:1 and `danger.text` `#FF6A7C` 4.0:1. Tertiary becomes `#A0A8B9` (4.6:1 on a card, 5.0:1 on a
 * filled control) and danger text the palette's own `#FF8190` (`danger.solid-hover`, 4.6:1). Every surface and edge stays the
 * apps'.
 */
export const WEB_DARK_COLORS = {
    ...APP_DARK_COLORS,
    'text.tertiary': '#A0A8B9',
    'danger.text': '#FF8190',
    /*
     * D803 (owner 2026-10-28, "não consigo ler"): a contrast sweep of the consoles in the dark. The menus' section headings (scope,
     * notifications, theme, the governed-rules groups) are `text.disabled`, and the wall's `#54575F` read 1.5:1 on a card - it
     * becomes `#808AA0` (3.2:1 on a card, still a step below tertiary, so a disabled field stays visibly off). The light sidebar of
     * the dark draws navy at an alpha over snow, where the alphas tuned for white-on-navy read thinner: the inactive sub-items
     * (`on-inverse.tertiary`, 4.0:1) take .72 (5.4:1) and the operator's e-mail (`on-inverse.muted`, 3.3:1) .68 (4.8:1).
     */
    'text.disabled': '#808AA0',
    'on-inverse.tertiary': 'rgba(35, 48, 72, 0.72)',
    'on-inverse.muted': 'rgba(35, 48, 72, 0.68)',
};
/** The color roles that are permitted to carry a chromatic (non-neutral) value: the semantic families. */
export const SEMANTIC_ROLES = [
    'success',
    'success.soft',
    'success.text',
    'success.hover',
    'success.press',
    'success.pulse',
    'danger',
    'danger.soft',
    'danger.text',
    'danger.hover',
    'danger.press',
    'danger.solid',
    'danger.solid-hover',
    'danger.solid-press',
    'danger.badge',
    'warning',
    'warning.soft',
    'warning.text',
];
/** Whether a role is one of the semantic families (the only roles allowed to be chromatic). */
export function isSemanticRole(role) {
    return SEMANTIC_ROLES.includes(role);
}
//# sourceMappingURL=color.js.map